<script setup lang="ts">
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { COVER_PAGE_INDEX, getCurrentPostCid } from '@@/bits/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import type { Page } from '@@/types/slideshow'
import { PageType } from '@@/types/slideshow'
import SurfaceSlideshowPageCover from '@@/vuecomponents/SurfaceSlideshowPageCover.vue'
import SurfaceSlideshowPagePostV2 from '@@/vuecomponents/SurfaceSlideshowPagePostV2.vue'
import SurfaceSlideshowPageSection from '@@/vuecomponents/SurfaceSlideshowPageSection.vue'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { range } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    // Needs to be a prop because the value may come from slideshowRealtime or slideshow store
    currentPageIndex: number

    /**
     * Determines how many pages to render before/after the current page.
     * This allows pages with attachments to preload in the background.
     * Background rendering is done using visibility: hidden.
     *
     * We use `visibility` over other properties because we want the browser to:
     * - Compute the layout and dimensions of elements ("display: none" doesn't)
     * - Ignore pointer/keyboard events for hidden pages ("opacity: 0" doesn't)
     * - Visibility is the only css property that can disable keyboard events
     * Reference: https://stackoverflow.com/questions/14731049/visibilityhidden-vs-displaynone-vs-opacity0
     */
    overscanPageLimit?: number
  }>(),
  {
    // 5 seems to be a good middle ground between reducing load time and avoiding performance issues
    // (Preloads in total 10 because it's 5 before and 5 after the current slide)
    overscanPageLimit: 5,
  },
)

const { lastPageIndex, pages } = useSlideshowPages()

const minIndex = computed(() => Math.max(COVER_PAGE_INDEX, props.currentPageIndex - props.overscanPageLimit))
const maxIndex = computed(() => Math.min(lastPageIndex.value, props.currentPageIndex + props.overscanPageLimit))
const renderRange = computed(() => range(minIndex.value, maxIndex.value + 1))

// entityId alone is not enough since there can be duplicates across entity types
const getPageKey = (page: Page) => page.pageType + page.entityId
const getIsCurrentPage = (pageIndex: number) => props.currentPageIndex === pageIndex

// currentPageRef is used in slideshow_navigation to support focus on content for a11y
const currentPageRef = ref<HTMLDivElement>()
defineExpose({ currentPageRef })

const { fontId } = storeToRefs(useSurfaceStore())
const fontClass = computed(() => tailwindSurfaceFontClass(fontId.value))
</script>

<template>
  <div data-testid="surfaceSlideshowContent" :class="['grid', 'grid-cols-1', 'overflow-hidden', fontClass]">
    <template v-for="i in renderRange">
      <div
        :key="getPageKey(pages[i])"
        :ref="getIsCurrentPage(i) ? 'currentPageRef' : undefined"
        :class="[
          'overflow-hidden',
          'min-h-0', // Allows grid item to shrink and not overflow
          'row-start-1 col-start-1', // Allows each page to render on top of each other in the same position
          !getIsCurrentPage(i) && 'invisible', // Only show one page at a time
        ]"
        tabindex="-1"
      >
        <SurfaceSlideshowPageCover
          v-if="pages[i].pageType === PageType.Cover"
          :is-currently-viewed="getIsCurrentPage(i)"
        />
        <SurfaceSlideshowPageSection
          v-else-if="pages[i].pageType === PageType.Section"
          :section-id="Number(pages[i].entityId)"
        />
        <template v-else-if="pages[i].pageType === PageType.Post">
          <SurfaceSlideshowPagePostV2
            :post-cid="getCurrentPostCid(String(pages[i].entityId))"
            :is-currently-viewed="getIsCurrentPage(i)"
          />
        </template>
      </div>
    </template>
  </div>
</template>
